import React from "react";
import { Link } from "react-router-dom";
import {
  MdPlayArrow,
  MdStop,
  MdList,
  MdSettings,
  MdArrowForward,
} from "react-icons/md";
import { Card } from "@/components/ui/Card";
import { useTaskList } from "../store/useTaskList";
import { CardSkeleton } from "@/components/ui/Skeleton";
import { StatusBadge } from "@/components/ui/StatusBadge";

const DashboardPage: React.FC = () => {
  const { tasks, total, loading } = useTaskList();

  const safeTasks = tasks ?? [];

  const running = safeTasks.filter((t) => t.status === "RUNNING").length;
  const stopped = safeTasks.filter((t) => t.status === "STOPPED").length;

  const stats = [
    {
      label: "Total Tasks",
      value: total,
      icon: MdList,
      color: "bg-indigo-50 text-indigo-600",
    },
    {
      label: "Running",
      value: running,
      icon: MdPlayArrow,
      color: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Stopped",
      value: stopped,
      icon: MdStop,
      color: "bg-gray-100 text-gray-500",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
          : stats.map(({ label, value, icon: Icon, color }) => (
              <Card key={label}>
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}
                  >
                    <Icon size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{value}</p>
                    <p className="text-xs text-gray-400">{label}</p>
                  </div>
                </div>
              </Card>
            ))}
      </div>

      {/* Recent Tasks */}
      <Card
        title="Recent Tasks"
        subtitle="Last 5 analysis tasks"
        headerRight={
          <Link
            to="/tasks"
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
          >
            View all <MdArrowForward size={14} />
          </Link>
        }
      >
        {loading ? (
          <CardSkeleton />
        ) : tasks.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            No tasks available
          </p>
        ) : (
          <div className="space-y-3">
            {tasks.slice(0, 5).map((task) => (
              <div
                key={task.taskId}
                className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {task.deviceName}
                  </p>
                  <p className="text-xs text-gray-400">{task.taskId}</p>
                </div>
                <StatusBadge status={task.status} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/tasks">
          <Card className="hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center">
                <MdList size={18} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Task List</p>
                <p className="text-xs text-gray-400">
                  View and manage all tasks
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/config">
          <Card className="hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center">
                <MdSettings size={18} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Algorithm Config
                </p>
                <p className="text-xs text-gray-400">View task parameters</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default DashboardPage;
